import { ChevronDown, ChevronUp, MoreVerticalIcon, PlusIcon, SearchIcon, X } from "lucide-react";
import React, { JSX, useCallback, useRef } from "react";
import { useEffect, useState } from "react";
import queryString from "query-string";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import apiService from "../../services/apiService";
import {
  DragAndDropUpload,
  DragAndDropIcon,
  DragAndDropHelpText,
  DragAndDropTitle,
  UploadPreview,
  DragAndDropMaxSizeText,
} from "../../pages/customer-details/components/UI";
import { toast } from "react-toastify";
import { getLocalStorageItem, getFileExtension } from "../../utils";
import axios from "axios";
import ReactSelect from "react-select";
import { Separator } from "../../components/ui/separator";
import { PolicyTable } from "./PolicyTable";
import { InvestmentPreferenceSheet } from "./InvestmentPreferenceSheet";
import { ManagedLender, LenderPolicy, InvestmentPreference, normalizeLenderPolicy } from "../../types/lender";

import LenderBankAccountsSheet from "./LenderBankAccountsSheet";
interface User {
  is_active: string;
  name: string;
  email: string;
  mobile: string;
  designation_id: string;
  status: "Active" | "Inactive";
  lender_user_product_id: string;
  initials: string;
  location: string;
  id: number;
}

interface PaginationData {
  currentPage: number;
  totalCustomers: number;
  pageSize: number;
  totalPages: number;
}
interface LenderTypes {
  id: number;
  name: string;
  display_order: number;
}

interface LenderUserProductType {
  id: number;
  name: string;
  display_order: number;
}

interface FileDetails {
  name: string;
  size: number;
  fileType: string;
}

interface PresignedUrlResponse {
  data: {
    preSignedUrl: string;
  };
}

interface Lender {
  id: number;
  name: string;
  initials: string;
  avatar: string | null;
  users: User[];
  created_at: string;
  rm_name: string;
  policies?: LenderPolicy[];
  profile: NonNullable<{
    company_name: string;
    zone: string;
    gst_number: string;
    address_line1: string;
    address_city: string;
    address_state: string;
    address_pincode: string;
    document_pan: File | null;
    document_aadhar: File | null;
    document_gst: File | null;
    document_msme: File | null;
    payout_percentage: number;
    payout_type: string;
    agreement: File | null;
    lender_type_id: string;
    product: string;
    entity_id?: string;
    entity_id_type?: string;
  }>;
}

interface ApiDocument {
  name: string;
  sizeInBytes: number;
  documentType: string;
  documentCategory: string;
  status: string;
  signedUrl: string;
  createdAt: string;
  updatedAt: string;
  imageUrl: string;
  s3FileKey: string; // Added property
}

interface ApiResponse {
  data: ApiDocument[];
}

interface LenderTypesResponse {
  data: LenderTypes[];
}

interface LenderDesignationResponse {
  data: LenderDesignationTypes[];
}

interface LenderDesignationTypes {
  id: number;
  name: string;
  display_order: number;
}

interface CurrentFilter {
  page: number;
  pageSize: number;
  search?: string;
  relationshipManager?: string;
  location?: string;
  requirement?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
}

interface PostData {
  lender_id: string;
  fileDetails: FileDetails;
  documentType: string;
  documentCategory: string;
  [key: string]: unknown;
}

interface DocumentData {
  title: string;
  type: string;
  filename: string;
}

const Lenderaggreement: DocumentData[] = [
  {
    title: "AGGREEMENT",
    type: "LENDER_AGGREEMENT",
    filename: "aggreement",
  },
];

interface ApiDocument {
  name: string;
  sizeInBytes: number;
  documentType: string;
  documentCategory: string;
  status: string;
  signedUrl: string;
  createdAt: string;
  updatedAt: string;
}

interface Document {
  name: string;
  sizeInBytes: number;
  fileType: string;
  documentType: string;
  documentUrl: string;
  documentCategory: string;
  status: string;
  signedUrl?: string;
  imageUrl?: string;
  s3FileKey?: string;
}

export const ManageLenders = (): JSX.Element => {
  const [pincodeError, setPincodeError] = useState<string | null>(null);
  const [selectedLender, setSelectedLender] = useState<Lender | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isAddLenderOpen, setIsAddLenderOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isBankAccountsOpen, setIsBankAccountsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProfileSaved, setIsProfileSaved] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string>("");
  const [selectedEntityIdType, setSelectedEntityIdType] = useState<string>("");
  const [lenderAccountType, setLenderAccountType] = useState<"Current Account" | "Virtual Account">("Current Account");
  const [accountNumberMismatchError, setAccountNumberMismatchError] = useState<string | null>(null);
  const [profileBankErrors, setProfileBankErrors] = useState<Record<string, string>>({});
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [lender_type, setLenderType] = useState<LenderTypes[]>([]);
  const [lender_user_product_types, setLenderUserProductType] = useState<
    LenderUserProductType[]
  >([]);

  const [lender_designation, setLenderDesignation] = useState<
    LenderDesignationTypes[]
  >([]);
  const [expandedLenderId, setExpandedLenderId] = useState<number | null>(null);
  const zoneOptions = [
    { value: "North", label: "North" },
    { value: "South", label: "South" },
    { value: "East", label: "East" },
    { value: "West", label: "West" },
    { value: "Center", label: "Center" },
    { value: "AllIndia", label: "All India" },
  ];

  const LenderProduct = [
    { value: "Hybrid", label: "Hybrid" },
    { value: "Leasing", label: "Leasing" },
  ];

  // Form validation state
  const [addUserErrors, setAddUserErrors] = useState<Record<string, string>>(
    {}
  );
  const [editUserErrors, setEditUserErrors] = useState<Record<string, string>>(
    {}
  );
  const [addLenderErrors, setAddLenderErrors] = useState<
    Record<string, string>
  >({});
  const [entitySuggestions, setEntitySuggestions] = useState<
    Array<{ id: string; idType: string; name: string; status: string }>
  >([]);
  const [showEntitySuggestions, setShowEntitySuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const entityDropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Separate state for profile/institution name search
  const [profileEntitySuggestions, setProfileEntitySuggestions] = useState<
    Array<{ id: string; idType: string; name: string; status: string }>
  >([]);
  const [showProfileEntitySuggestions, setShowProfileEntitySuggestions] =
    useState(false);
  const [isProfileSearching, setIsProfileSearching] = useState(false);
  const profileEntityDropdownRef = useRef<HTMLDivElement>(null);
  const profileSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetLender, setSheetLender] = useState<ManagedLender | null>(null);
  const [sheetPolicy, setSheetPolicy] = useState<LenderPolicy | undefined>(undefined);
  // Handle click outside to close entity suggestions dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        entityDropdownRef.current &&
        !entityDropdownRef.current.contains(event.target as Node)
      ) {
        setShowEntitySuggestions(false);
      }
      if (
        profileEntityDropdownRef.current &&
        !profileEntityDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileEntitySuggestions(false);
      }
    }

    if (showEntitySuggestions || showProfileEntitySuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEntitySuggestions, showProfileEntitySuggestions]);

  useEffect(() => {
    const fetchLenderTypes = async () => {
      try {
        const response = await apiService.get<LenderTypesResponse>(
          `/lender/get-lender-types`
        );
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id,
          display_order: doc.display_order,
        }));
        setLenderType(transformedDocs);
      } catch (error) {
        console.error("Error fetching lender types:", error);
      }
    };

    const fetchLenderDesignation = async () => {
      try {
        const response = await apiService.get<LenderDesignationResponse>(
          `/lender/get-lender-designation-types`
        );
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id,
          display_order: doc.display_order,
        }));
        setLenderDesignation(transformedDocs);
      } catch (error) {
        console.error("Error fetching lender types:", error);
      }
    };

    const fetchLenderUserProductTypes = async () => {
      try {
        const response = await apiService.get<LenderTypesResponse>(
          `/lender/get-lender-user-product-types`
        );
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id,
          display_order: doc.display_order,
        }));
        setLenderUserProductType(transformedDocs);
      } catch (error) {
        console.error("Error fetching lender types:", error);
      }
    };

    fetchLenderTypes();
    fetchLenderDesignation();
    fetchLenderUserProductTypes();
  }, []);

  const ManageDate = (data: string) => {
    const rawDate = data;
    const formattedDate = new Date(rawDate)
      .toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
      .replace(",", "");

    return formattedDate; // Output: May 07 2025
  };

  const [filterTabs, setFilterTabs] = useState([
    {
      id: "total_institutions",
      label: "Total Institutions",
      count: 0,
      active: true,
    },
    { id: "total_user", label: "Total POCs", count: 0, active: false },
  ]);
  const [policyCount, setPolicyCount] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    designation_id: "",
    mca: "",
    org: "",
    product: "",
    lender_type_id: "",
    investment_preference: "",
    entity_id: "",
    entity_id_type: "",
  });

  const [addUserData, setAddUserData] = useState({
    userName: "",
    userEmail: "",
    userMobile: "",
    userDesignation_id: "",
    lender_company_id: "",
    userlocation: "",
    lender_user_product_id: "",
  });

  const handleApiCall = async (
    apiCall: () => Promise<any>,
    successMessage?: string
  ) => {
    try {
      const response = await apiCall();
      // Check if the response indicates success
      if (
        response.status === 200 ||
        response.status === "success" ||
        response.status === 201
      ) {
        if (successMessage) {
          toast.success(successMessage); // Optionally show a success message
        }
        return response;
      } else {
        toast("Something went wrong. Please try again.");
        // throw new Error("API call failed");
      }
    } catch (error: any) {
      console.error("API Error:", error.message); // Log the error for debugging
      if (
        (error.response && error.response.data && error.response.data.message,
        error.message)
      ) {
        // Display the error message from the backend
        toast.error(error.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
      // throw error;
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleLenderInputChange(e); // Use our new validation-aware handler
  };

  const handleChangeadduser = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setAddUserData((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Clear error for this field when it changes
    if (addUserErrors[id]) {
      setAddUserErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }
  };

  const handleLenderInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Clear error for this field when it changes
    if (addLenderErrors[id]) {
      setAddLenderErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }
  };

  const addlender = async () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Required fields validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.lender_type_id || formData.lender_type_id.trim() === "") {
      newErrors.lender_type_id = "Lender Type is required";
      isValid = false;
    }

    if (!formData.mca || formData.mca.trim() === "") {
      newErrors.mca = "MCA Registered Entity Name is required";
      isValid = false;
    }

    if (!formData.org || formData.org.trim() === "") {
      newErrors.org = "Organisation Name is required";
      isValid = false;
    }

    // Update errors state
    setAddLenderErrors(newErrors);

    // If validation fails, stop here
    if (!isValid) {
      return;
    }

    try {
      await handleApiCall(
        () =>
          apiService.post("/lender/create-lender", {
            ...formData,
          }),
        "Lender added successfully!"
      );

      // Reset the form and close the modal
      setFormData({
        name: "",
        email: "",
        mobile: "",
        designation_id: "",
        mca: "",
        org: "",
        product: "",
        lender_type_id: "",
        investment_preference: "",
        entity_id: "",
        entity_id_type: "",
      });
      setAddLenderErrors({});
      setIsAddLenderOpen(false);

      // Refresh the data
      await fetchPageDataWithFilters();
    } catch (error) {
      // Error handling is already done in handleApiCall
    }
  };

  const handleLenderClick = (lender: Lender) => {
    setSelectedLender(lender);
    setIsSidebarOpen(true);
  };

  const toggleLenderPolicies = (lenderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedLenderId((prev) => (prev === lenderId ? null : lenderId));
  };

  const handleViewProfile = (lender: Lender, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLender(lender);
    setIsProfileOpen(true);
    // Reset saved flag when opening profile to allow editing
    setIsProfileSaved(false);
    // Initialize selected entity values from saved profile data
    setSelectedEntityId((lender.profile as any)?.entity_id || "");
    setSelectedEntityIdType((lender.profile as any)?.entity_id_type || "");
    setLenderAccountType(
      (lender.profile as any)?.account_type === "Virtual Account" ? "Virtual Account" : "Current Account"
    );
    setAccountNumberMismatchError(null);
    setProfileBankErrors({});
    // fetchALlDocuments();
  };

  const handleManageBankAccounts = (lender: Lender, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLender(lender);
    setIsBankAccountsOpen(true);
  };

  const isBankDetailsLocked = (() => {
    if (!selectedLender?.profile) return false;
    const p = selectedLender.profile as any;
    const hasAccountNum = Boolean((p?.account_number ?? "").toString().trim() || (p?.virtual_account_number ?? "").toString().trim());
    return Boolean(
      p?.account_type &&
      (p?.bank_name ?? "").toString().trim() &&
      (p?.branch_name ?? "").toString().trim() &&
      (p?.account_holder_name ?? "").toString().trim() &&
      (p?.ifsc_code ?? "").toString().trim() &&
      hasAccountNum
    );
  })();

  useEffect(() => {
    if (isProfileOpen && selectedLender?.id) {
      fetchALlDocuments(); // Call fetchALlDocuments only after selectedLender is updated
    }
  }, [isProfileOpen, selectedLender?.id]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditUserOpen(true);
  };

  const getDocument = (
    type: string,
    documents: Document[] = []
  ): Document | undefined => {
    return documents.find((document) => document.documentType === type);
  };

  const handleDeactivateUser = async (user: User) => {
    if (!selectedLender) return;

    try {
      await handleApiCall(
        () =>
          apiService.post("/lender/deactivate-user", {
            id: user.id,
          }),
        "User deactivated successfully!"
      );

      // Update the state
      const updatedUsers = selectedLender.users.map((u) =>
        u.id === user.id ? { ...u, is_active: "0" } : u
      );
      setSelectedLender({
        ...selectedLender,
        users: updatedUsers,
      });
      setCustomers((prevLenders) =>
        prevLenders.map((lender) =>
          lender.id === selectedLender.id
            ? { ...lender, users: updatedUsers }
            : lender
        )
      );
    } catch (error) {}
  };

  const handleActivateUser = async (user: User) => {
    if (!selectedLender) return;

    try {
      await handleApiCall(
        () =>
          apiService.post("/lender/activate-user", {
            id: user.id,
          }),
        "User activated successfully!"
      );

      // Update the state
      const updatedUsers = selectedLender.users.map((u) =>
        u.id === user.id ? { ...u, is_active: "1" } : u
      );
      setSelectedLender({
        ...selectedLender,
        users: updatedUsers,
      });
      setCustomers((prevLenders) =>
        prevLenders.map((lender) =>
          lender.id === selectedLender.id
            ? { ...lender, users: updatedUsers }
            : lender
        )
      );
    } catch (error) {}
  };

  const handleUpdateUser = async (updatedUser: User) => {
    if (!selectedLender || !selectedUser) {
      toast.error("No lender or user selected.");
      return;
    }

    // Validate required fields
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!updatedUser.name || updatedUser.name.trim() === "") {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!updatedUser.email || updatedUser.email.trim() === "") {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedUser.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    setEditUserErrors(newErrors);

    if (!isValid) {
      return;
    }

    try {
      // Make the API call to update the user
      await handleApiCall(
        () =>
          apiService.post("/lender/update-lender-user", {
            ...updatedUser,
            lender_company_id: String(selectedLender.id),
          }),
        "User updated successfully!"
      );

      // Update the local state for the selected lender
      const updatedUsers = selectedLender.users.map((user) =>
        user.id === selectedUser.id ? updatedUser : user
      );

      setSelectedLender({
        ...selectedLender,
        users: updatedUsers,
      });

      // Refresh the data
      await fetchPageDataWithFilters();

      // Clear errors and close the edit user modal
      setEditUserErrors({});
      setIsEditUserOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(
        "An error occurred while updating the user. Please try again."
      );
    }
  };

  const [lenders, setCustomers] = useState<Lender[]>([]);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    currentPage: 1,
    totalCustomers: 0,
    pageSize: 25,
    totalPages: 1,
  });

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchPageDataWithFilters();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const [currentFilter, setCurrentFilter] = useState<CurrentFilter>({
    page: 1,
    pageSize: 25,
  });

  const fetchPageDataWithFilters = async () => {
    try {
      let filter = {
        ...currentFilter,
        search: searchQuery,
      };

      const response = await handleApiCall(() =>
        apiService.get<{
          customers: Lender[];
          pagination: PaginationData;
          policy_count?: number;
        }>(`/lender/fetchAllCustomers?${queryString.stringify(filter)}`)
      );

      // Update the state with the fetched data
      setCustomers(
        (response.data.customers ?? []).map((lender: Lender) => ({
          ...lender,
          policies: Array.isArray(lender.policies)
            ? lender.policies.map((policy) => normalizeLenderPolicy(policy))
            : [],
        }))
      );
      setPaginationData(response.data.pagination);
      setPolicyCount(response.data.policy_count ?? 0);

      setFilterTabs((prevTabs) =>
        prevTabs.map((tab) => {
          if (tab.id === "total_institutions") {
            return { ...tab, count: response.data.pagination.totalCustomers }; // Update total institutions
          } else if (tab.id === "total_user") {
            return { ...tab, count: response.data.pagination.totalUser }; // Update total users
          }
          return tab; // Return unchanged tab for other cases
        })
      );
    } catch (error) {
      toast.error("An error occurred while fetching data. Please try again.");
    }
  };

  const addlenderuser = async () => {
    if (!selectedLender?.id) {
      toast.error("No lender selected.");
      return;
    }

    // Validate required fields
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Required fields validation
    if (!addUserData.userName || addUserData.userName.trim() === "") {
      newErrors.userName = "Name is required";
      isValid = false;
    }

    if (!addUserData.userEmail || addUserData.userEmail.trim() === "") {
      newErrors.userEmail = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addUserData.userEmail)) {
      newErrors.userEmail = "Please enter a valid email address";
      isValid = false;
    }

    // Update errors state
    setAddUserErrors(newErrors);

    // If validation fails, stop here
    if (!isValid) {
      return;
    }

    try {
      const response = await handleApiCall(
        () =>
          apiService.post("/lender/create-lender-user", {
            ...addUserData,
            lender_company_id: String(selectedLender.id),
          }),
        "User added successfully!"
      );

      const newUser = response.userdetails;

      // Reset the form and clear errors
      setAddUserData({
        userName: "",
        userEmail: "",
        userMobile: "",
        userDesignation_id: "",
        lender_company_id: "",
        userlocation: "",
        lender_user_product_id: "",
      });
      setAddUserErrors({});
      setIsAddUserOpen(false);

      // Update the state
      setSelectedLender((prev) =>
        prev
          ? {
              ...prev,
              users: [newUser, ...prev.users], // Add the new user at the beginning
            }
          : null
      );
      setCustomers((prevLenders) =>
        prevLenders.map((lender) =>
          lender.id === selectedLender.id
            ? { ...lender, users: [newUser, ...lender.users] } // Add the new user at the beginning
            : lender
        )
      );
    } catch (error) {
      // Error handling is already in handleApiCall
    }
  };

  useEffect(() => {
    fetchPageDataWithFilters();
  }, [currentFilter]);

  const fetchALlDocuments = useCallback(() => {
    if (!selectedLender?.id) return;

    apiService
      .get<ApiResponse>(`/lender/company/${selectedLender.id}/documents`)
      .then((response) => {
        const transformedDocs = response.data.map((doc) => ({
            name: doc.name,
            sizeInBytes: doc.sizeInBytes,
            fileType: doc.name.split(".").pop() || "",
            documentType: doc.documentType,
            documentUrl: doc.imageUrl,
            documentCategory: doc.documentCategory,
            status: doc.status,
            signedUrl: doc.signedUrl,
            imageUrl: doc.imageUrl,
            s3FileKey: doc.s3FileKey,
          }));
        setDocuments(transformedDocs);
      })
      .catch(() => {});
  }, [selectedLender?.id]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], documentType: string, fileName?: string) => {
      const documentCategory = "LENDER_AGGREEMENT"; // Adjust this if needed

      try {
        for (const file of acceptedFiles) {
          const fileDetails: FileDetails = {
            name: fileName
              ? `${fileName}.${getFileExtension(file.type)}`
              : file.name,
            size: file.size,
            fileType: file.type,
          };

          const postData: PostData = {
            lender_id: String(selectedLender?.id) || "",
            fileDetails,
            documentType,
            documentCategory,
          };

          // Generate presigned URL and upload the file
          const response = await apiService.post<PresignedUrlResponse>(
            "/lender/generatePresignedUrl",
            postData
          );

          await axios.put(response.data.preSignedUrl, file, {
            headers: {
              "Content-Type": file.type,
            },
          });

          // Confirm the document upload
          const pathBody = {
            lender_id: selectedLender?.id,
            fileMetadata: {
              documentType,
              documentCategory,
              status: "UPLOADED_TO_S3",
            },
            loggedInUser: getLocalStorageItem("detail")?.userId,
          };

          await apiService.patch("/lender/confirmDocumentUpload", pathBody);
        }

        // Refresh the document list
        if (selectedLender?.id) {
          fetchALlDocuments();
        }

        toast.success("Documents uploaded successfully!");
      } catch (error) {
        toast.error(
          "An error occurred while uploading documents. Please try again."
        );
      }
    },
    [selectedLender?.id, fetchALlDocuments]
  );

  const getInitials = (name: string): string => {
    if (!name || typeof name !== "string") return ""; // Handle empty or invalid names
    const words = name.trim().split(/\s+/); // Split by spaces and remove extra spaces
    return words
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join(""); // Get first letters of the first two words
  };

  const handleZoneChange = (value: string) => {
    if (selectedLender?.profile) {
      setSelectedLender({
        ...selectedLender,
        profile: {
          ...selectedLender.profile,
          zone: value,
        },
      });
    }
  };

  const handleProductChange = (value: string[]) => {
    if (selectedLender?.profile) {
      setSelectedLender({
        ...selectedLender,
        profile: {
          ...selectedLender.profile,
          product: value.join(","),
        },
      });
    }
  };

  const handlePayoutChange = (value: string) => {
    if (selectedLender?.profile) {
      setSelectedLender({
        ...selectedLender,
        profile: {
          ...selectedLender.profile,
          payout_type: value,
        },
      });
    }
  };

  const handleProfileInputChange = (
    field: keyof Lender["profile"],
    value: string
  ) => {
    if (field === "address_pincode") {
      const pincodeRegex = /^[1-9][0-9]{5}$/; // Regex for a valid 6-digit pincode
      if (!pincodeRegex.test(value)) {
        setPincodeError(
          "Invalid pincode. Please enter a valid 6-digit number."
        );
      } else {
        setPincodeError(null); // Clear the error if valid
      }
    }

    if (selectedLender?.profile) {
      setSelectedLender({
        ...selectedLender,
        profile: {
          ...selectedLender.profile,
          [field]: value,
        },
      });
    }
  };

  const handledisplayname = (value: string) => {
    if (selectedLender?.profile) {
      setSelectedLender({
        ...selectedLender,
        name: value,
      });
    }
  };

  const saveProfileChanges = async () => {
    if (!selectedLender?.profile) return;
    const profile = selectedLender.profile as Record<string, unknown>;
    setProfileBankErrors({});
    const isCurrent = lenderAccountType === "Current Account";
    const accountNum = (isCurrent ? profile.account_number : profile.virtual_account_number) as string | undefined;
    const accountNumConfirm = (isCurrent ? profile.account_number_confirm : profile.virtual_account_number_confirm) as string | undefined;
    const bankName = (profile.bank_name as string)?.trim?.() ?? "";
    const branchName = (profile.branch_name as string)?.trim?.() ?? "";
    const accountHolderName = (profile.account_holder_name as string)?.trim?.() ?? "";
    const ifscCode = (profile.ifsc_code as string)?.trim?.() ?? "";
    const errors: Record<string, string> = {};
    // Bank block: if user starts filling any bank field, all bank fields become required; if all empty, allow save (basic profile only).
    const hasAnyBankInput = Boolean(
      accountHolderName ||
        bankName ||
        branchName ||
        ifscCode ||
        (accountNum?.trim?.() ?? "") ||
        (accountNumConfirm?.trim?.() ?? "")
    );
    if (!hasAnyBankInput) {
      setAccountNumberMismatchError(null);
    }
    if (hasAnyBankInput) {
      if (!lenderAccountType) errors.account_type = "Account type is required";
      if (!accountNum?.trim?.())
        errors.account_number =
          (isCurrent ? "Account number" : "Virtual account number") + " is required";
      if (!accountNumConfirm?.trim?.())
        errors.account_number_confirm = "Re-enter account number is required";
      if (accountNum?.trim?.() && accountNumConfirm?.trim?.() && accountNum !== accountNumConfirm) {
        errors.account_number_confirm = "Account numbers do not match";
      }
      if (!bankName) errors.bank_name = "Bank name is required";
      if (!branchName) errors.branch_name = "Branch name is required";
      if (!accountHolderName) errors.account_holder_name = "Account holder name is required";
      if (!ifscCode) errors.ifsc_code = "IFSC code is required";
    }
    if (Object.keys(errors).length > 0) {
      setProfileBankErrors(errors);
      return;
    }
    const {
      account_number,
      account_number_confirm,
      virtual_account_number,
      virtual_account_number_confirm,
      ...restProfile
    } = profile;
    const profilePayload = {
      ...restProfile,
      product: selectedLender.profile.product,
      entity_id: selectedEntityId || (profile.entity_id as string) || "",
      entity_id_type: selectedEntityIdType || (profile.entity_id_type as string) || "",
      account_type: lenderAccountType,
      account_holder_name: profile.account_holder_name,
      bank_name: profile.bank_name,
      branch_name: profile.branch_name,
      ifsc_code: profile.ifsc_code,
      ...(isCurrent
        ? {
            account_number: profile.account_number,
            account_number_confirm: profile.account_number_confirm,
          }
        : {
            virtual_account_number: profile.virtual_account_number,
            virtual_account_number_confirm: profile.virtual_account_number_confirm,
          }),
    };
    try {
      await handleApiCall(
        () =>
          apiService.post(`/lender/update-profile`, {
            id: selectedLender.id,
            name: selectedLender.name,
            profile: profilePayload,
          }),
        "Profile updated successfully!"
      );
      // Update selectedLender with entity_id and entity_id_type after successful save
      if (selectedEntityId || (selectedLender.profile as any).entity_id) {
        setSelectedLender({
          ...selectedLender,
          profile: {
            ...selectedLender.profile,
            entity_id: selectedEntityId || (selectedLender.profile as any).entity_id || "",
            entity_id_type: selectedEntityIdType || (selectedLender.profile as any).entity_id_type || "",
          } as typeof selectedLender.profile & {
            entity_id?: string;
            entity_id_type?: string;
          },
        });
        setIsProfileSaved(true);
      }
      setIsProfileOpen(false);
      await fetchPageDataWithFilters();
    } catch (error) {}
  };

  const handlePageChange = (newPage: number) => {
    setCurrentFilter((prev) => ({
      ...prev,
      page: newPage, // Update the current page
    }));
  };

  const handleAddUserOpen = (isOpen: boolean) => {
    setIsAddUserOpen(isOpen);
    if (isOpen) {
      setAddUserData({
        userName: "",
        userEmail: "",
        userMobile: "",
        userDesignation_id: "",
        lender_company_id: "",
        userlocation: "",
        lender_user_product_id: "",
      });
      setAddUserErrors({}); // Clear errors when opening the modal
    }
  };

  const handleAddLenderOpen = (isOpen: boolean) => {
    setIsAddLenderOpen(isOpen);
    if (!isOpen) {
      // Reset form data and errors when closing
      setFormData({
        name: "",
        email: "",
        mobile: "",
        designation_id: "",
        mca: "",
        org: "",
        product: "",
        lender_type_id: "",
        investment_preference: "",
        entity_id: "",
        entity_id_type: "",
      });
      setAddLenderErrors({});
    }
  };

  const handleEditUserChange = (field: string, value: string) => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, [field]: value });

      // Clear error for this field
      if (editUserErrors[field]) {
        setEditUserErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    }
  };

  const handleEditUserOpen = (isOpen: boolean) => {
    setIsEditUserOpen(isOpen);
    if (!isOpen) {
      setEditUserErrors({}); // Clear errors when closing the modal
      setSelectedUser(null);
    }
  };

  useEffect(() => {
    if (!isProfileOpen || !isSidebarOpen) {
      document.body.removeAttribute("data-scroll-locked");
      document.body.style.pointerEvents = "auto";
      document.body.style.overflow = "auto";
    }
  }, [isProfileOpen, isSidebarOpen]);

  const openCreate = (lender: ManagedLender) => {
    setSheetLender(lender);
    setSheetPolicy(undefined);
    setSheetOpen(true);
  };

  const openEdit = (lender: ManagedLender, policy: LenderPolicy) => {
    setSheetLender(lender);
    setSheetPolicy(policy);
    setSheetOpen(true);
  };

  return (
    <div className="bg-white flex flex-row justify-center w-full">
      <div className="bg-white w-full max-w-[1440px] p-6">
        <div className="w-full max-w-[1376px] mx-auto">
          <div className="flex items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-800 font-['Poppins',Helvetica]">
              Lenders
            </h1>

            <Separator orientation="vertical" className="mx-4 h-[55px]" />

            <div className="flex items-center space-x-1">
              <ToggleGroup
                type="single"
                defaultValue="total"
                className="border border-neutral-200 rounded-lg p-1"
              >
                {filterTabs.map((tab) => (
                  <ToggleGroupItem
                    key={tab.id}
                    value={tab.id}
                    className={`w-fit rounded-lg whitespace-nowrap ${
                      tab.active ? "bg-[#3f8b9833] text-[#3f8b98]" : ""
                    }`}
                  >
                    <span className="font-['Poppins',Helvetica] font-medium text-base">
                      {tab.label} ({tab.count})
                    </span>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <Button
                variant="outline"
                className="rounded-lg border border-neutral-200 h-auto px-4 py-2 bg-[#3f8b9833] text-[#3f8b98] hover:bg-[#3f8b9844] hover:text-[#3f8b98] font-['Poppins',Helvetica] font-medium text-base"
              >
                Policies ({policyCount})
              </Button>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {isSearchExpanded ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="Search Lender or users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-[300px] "
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setIsSearchExpanded(false);
                      setSearchQuery("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-xl border-[#3f8b98]"
                  onClick={() => setIsSearchExpanded(true)}
                >
                  <SearchIcon className="h-4 w-4" />
                </Button>
              )}
              <Button
                className="rounded-xl bg-[#3f8b98] text-white font-['Poppins',Helvetica] font-medium hover:bg-[#357a85] cursor-pointer"
                onClick={() => handleAddLenderOpen(true)}
              >
                <PlusIcon className="mr-2 h-3.5 w-3.5" />
                Add Lender
              </Button>
            </div>
          </div>

          <Card className="rounded-2xl border-neutral-200 shadow-[0px_1px_2px_#0000000d]">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-neutral-200">
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-4 w-[48px]" />

                    <TableHead
                      className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[260px]"
                      colSpan={2}
                    >
                      Lender Name
                    </TableHead>

                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[150px]">
                      Lender Type
                    </TableHead>

                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[220px]">
                      User Name
                    </TableHead>

                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[150px]">
                      AF Lender POC
                    </TableHead>

                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[200px]">
                      Product
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[200px]">
                      Policies
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[140px]">
                      Created On
                    </TableHead>

                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[100px]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lenders.map((lender) => {
                    const lenderPolicies = lender.policies ?? [];
                    const activePolicyCount = lenderPolicies.filter((p) => p.active).length;
                    const totalPolicyCount = lenderPolicies.length;
                    const isExpanded = expandedLenderId === lender.id;

                    return (
                      <React.Fragment key={lender.id}>
                        <TableRow
                          className="border-b border-neutral-200 cursor-pointer hover:bg-gray-50"
                          onClick={() => handleLenderClick(lender)}
                        >
                          <TableCell className="py-4 pl-4 w-[48px]">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-800"
                              onClick={(e) => toggleLenderPolicies(lender.id, e)}
                              aria-label={isExpanded ? "Collapse policies" : "Expand policies"}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="py-4 pl-6 w-[100px]">
                        {lender.avatar ? (
                          <Avatar
                            className="rounded-lg"
                            style={{ height: "50px", width: "80px" }}
                          >
                            <AvatarImage
                              src={
                                lender.avatar
                                  ? lender.avatar.startsWith("http")
                                    ? lender.avatar
                                    : `${import.meta.env.VITE_API_BASE_URL}/${
                                        lender.avatar
                                      }`
                                  : ""
                              }
                              alt={lender.name}
                              className="object-contain"
                            />
                            {/* <AvatarFallback>{getInitials(lender.name)}</AvatarFallback> */}
                          </Avatar>
                        ) : (
                          <></>
                          // <div className="h-8 w-8 rounded-lg bg-[#8c650033] flex items-center justify-center">
                          //   <span className="text-sm">{getInitials(lender.name)}</span>
                          // </div>
                        )}
                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-800 text-base">
                        {lender.name}
                      </TableCell>

                      <TableCell className="py-4 pl-6">
                        {lender_type.find(
                          (type) =>
                            type.id === Number(lender?.profile.lender_type_id)
                        )?.name || "-"}
                      </TableCell>

                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-base">
                        <span className="text-gray-600">
                          {lender?.users[0]?.name}
                        </span>
                        {lender.users.length > 1 && (
                          <>
                            <span className="text-gray-600">, </span>
                            <span className="font-medium text-[#3f8b98]">
                              +{lender.users.length - 1} more
                            </span>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">
                        {lender.rm_name}
                      </TableCell>

                      {/* <TableCell className="py-4 pl-6">
                        <Badge
                          variant="outline"
                          className={`rounded-full bg-${lender.profile.product === "Hybrid" ? "[#3f8b981a]" : lender.profile.product === "Hybrid" ? "[#8c6500]" : "[#8c65001a]"
                            } text-${lender.profile.product === "Hybrid" ? "[#3f8b98]" : lender.profile.product === "Hybrid" ? "[#8c6500]" : "[#808080]"
                            } border-0 px-3 py-1 font-normal text-sm`}
                        >

                          {lender?.profile.product === "Hybrid" || lender?.profile.product === "Leasing"
                            ? lender.profile.product
                            : "-"}
                        </Badge>


                      </TableCell> */}

                      <TableCell className="py-4 pl-6">
                        {lender.profile?.product
                          ? lender.profile.product
                              .split(",") // Split the product string into an array
                              .map((product, index) => (
                                <Badge
                                  key={index}
                                  variant="outline"
                                  className={`rounded-full ${
                                    product === "Hybrid"
                                      ? "bg-[#3f8b981a] text-[#3f8b98]" // Consistent color for Hybrid
                                      : product === "Leasing"
                                      ? "bg-[#8c65001a] text-[#8c6500]" // Color for Leasing
                                      : "" // No color for undefined or other values
                                  } border-0 px-3 py-1 font-normal text-sm mr-2`}
                                >
                                  {product}
                                </Badge>
                              ))
                          : "-"}{" "}
                        {/* Display "-" if no product is set */}
                      </TableCell>

                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">
                        {totalPolicyCount > 0
                          ? `${activePolicyCount}/${totalPolicyCount}`
                          : "0/0"}
                      </TableCell>

                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">
                        {ManageDate(lender.created_at)}
                      </TableCell>
                      <TableCell className="py-4 pl-6">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVerticalIcon className="h-4 w-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem>Manage User</DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => handleManageBankAccounts(lender, e)}
                            >
                              Manage Bank Accounts
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              onClick={(e: any) => handleViewProfile(lender, e)}
                            >
                              View Profile
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="border-b border-neutral-200 bg-gray-50/60">
                        <TableCell colSpan={10} className="px-6 py-4">
                          <PolicyTable
                            lenderId={String(lender.id)}
                            policies={lenderPolicies}
                            onAdd={() => openCreate(lender)}
                            onEdit={(policy) => openEdit(lender, policy)}
                            onDuplicated={fetchPageDataWithFilters}
                            onDeleted={fetchPageDataWithFilters}
                            onStatusChanged={fetchPageDataWithFilters}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <div className="text-sm text-gray-500 font-['Poppins',Helvetica]">
                  {paginationData.totalCustomers === 0 ? (
                    "Showing 0 of 0 entries"
                  ) : (
                    <>
                      Showing{" "}
                      {(paginationData.currentPage - 1) *
                        paginationData.pageSize +
                        1}{" "}
                      to{" "}
                      {Math.min(
                        paginationData.currentPage * paginationData.pageSize,
                        paginationData.totalCustomers
                      )}{" "}
                      of {paginationData.totalCustomers} entries
                    </>
                  )}
                </div>
                <div className="ml-auto">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          className="border rounded-lg border-[#3f8b98] text-[#3f8b98] h-[34px] w-24 font-['Poppins',Helvetica] font-normal"
                          onClick={() =>
                            paginationData.currentPage > 1 &&
                            handlePageChange(paginationData.currentPage - 1)
                          }
                        >
                          <span>Previous</span>
                        </PaginationPrevious>
                      </PaginationItem>
                      {Array.from(
                        { length: paginationData.totalPages },
                        (_, index) => (
                          <PaginationItem key={index}>
                            <PaginationLink
                              isActive={
                                paginationData.currentPage === index + 1
                              }
                              className={`rounded-lg h-8 w-[30px] ${
                                paginationData.currentPage === index + 1
                                  ? "bg-[#3f8b98] text-white "
                                  : "border border-[#3f8b98] text-[#3f8b98]"
                              } font-['Poppins',Helvetica] font-normal`}
                              onClick={() => handlePageChange(index + 1)}
                            >
                              {index + 1}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      )}
                      <PaginationItem>
                        <PaginationNext
                          className="border rounded-lg border-[#3f8b98] text-[#3f8b98] h-[34px] w-16 font-['Poppins',Helvetica] font-normal"
                          onClick={() =>
                            paginationData.currentPage <
                              paginationData.totalPages &&
                            handlePageChange(paginationData.currentPage + 1)
                          }
                        >
                          Next
                        </PaginationNext>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lender Details Sidebar */}
      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetContent className="w-fit">
          <SheetHeader className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SheetTitle className="text-xl font-semibold">
                  Manage Users
                </SheetTitle>
                <h3 className="text-xl text-gray-500">
                  {selectedLender?.name}
                </h3>
              </div>
              <Button
                className="mr-[25px] rounded-xl bg-[#3f8b98] text-white font-['Poppins',Helvetica] font-medium"
                onClick={() => handleAddUserOpen(true)}
              >
                <PlusIcon className="mr-2 h-3.5 w-3.5 " />
                Add User
              </Button>
            </div>
          </SheetHeader>

          {selectedLender && (
            <div className="mt-6">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-neutral-200">
                    <TableHead className="w-[170px]">User Name</TableHead>
                    <TableHead className="w-[200px]">Email</TableHead>
                    <TableHead className="w-[150px]">Mobile</TableHead>
                    <TableHead className="w-[180px]">Designation</TableHead>
                    <TableHead className="w-[130px]">Location</TableHead>
                    <TableHead className="w-[150px]">Product</TableHead>
                    <TableHead className="w-[90px]">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                {/* <div className="w-full max-h-[300px] overflow-y-auto"> */}
                <TableBody>
                  {selectedLender.users.map((user, index) => (
                    <TableRow
                      key={index}
                      className="border-b border-neutral-200"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-[#8c650033] flex items-center justify-center">
                            <span className="text-sm">
                              {getInitials(user.name)}
                            </span>
                          </div>
                          <span>{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.mobile}</TableCell>

                      <TableCell>
                        {lender_designation.find(
                          (designation) =>
                            designation.id === Number(user.designation_id)
                        )?.name || "-"}
                      </TableCell>
                      <TableCell>{user.location}</TableCell>
                      <TableCell>
                        {lender_user_product_types.find(
                          (product_type) =>
                            product_type.id ===
                            Number(user.lender_user_product_id)
                        )?.name || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`rounded-full ${
                            user.is_active
                              ? "bg-[#3f8b981a] text-[#3f8b98]"
                              : "bg-[#8c65001a] text-[#8c6500]"
                          } border-0 px-3 py-1 font-normal text-sm`}
                        >
                          {user.is_active
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVerticalIcon className="h-4 w-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onClick={() => handleEditUser(user)}
                            >
                              Edit User
                            </DropdownMenuItem>
                            {user.is_active ? (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDeactivateUser(user)}
                              >
                                Deactivate User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-green-600"
                                onClick={() => handleActivateUser(user)}
                              >
                                Activate User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {/* </div> */}
              </Table>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Lender Profile Sidebar */}
      <Sheet open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <SheetContent className="w-[900px] max-w-[75vw] overflow-y-auto">
          <SheetHeader className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SheetTitle>Lender Profile</SheetTitle>
                <h3 className="text-xl text-gray-500">
                  {selectedLender?.name}
                </h3>
              </div>
            </div>
          </SheetHeader>
          {selectedLender?.profile && (
            <div className="space-y-8">
              {/* Basic Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lender Type</Label>
                    <Select
                      defaultValue={
                        selectedLender?.profile?.lender_type_id || ""
                      }
                      onValueChange={(value: any) =>
                        handleProfileInputChange("lender_type_id", value)
                      }
                    >
                      <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                        <SelectValue>
                          {lender_type.find(
                            (option) =>
                              String(option.id) ===
                              String(selectedLender?.profile?.lender_type_id)
                          )?.name || "Select Lender Type"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                        {lender_type.map((option) => (
                          <SelectItem
                            className="hover:bg-gray-100"
                            key={option.id}
                            value={String(option.id)}
                          >
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Institution Name</Label>
                    <div className="relative">
                      <Input
                        disabled={selectedLender?.profile?.entity_id ? true : false}
                        placeholder="Enter institution name"
                        value={selectedLender?.profile?.company_name || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleProfileInputChange("company_name", value);

                          // Call search API after 3 characters
                          if (value.trim().length > 3) {
                            // Use debounce to prevent multiple API calls
                            if (profileSearchTimeout.current) {
                              clearTimeout(profileSearchTimeout.current);
                            }

                            profileSearchTimeout.current = setTimeout(() => {
                              setIsProfileSearching(true);
                              apiService
                                .post(`/customers/company/search`, { query: value })
                                .then((response) => {
                                  setProfileEntitySuggestions(
                                    (response as { data: any[] }).data || []
                                  );
                                  setShowProfileEntitySuggestions(true);
                                  setIsProfileSearching(false);
                                })
                                .catch((error) => {
                                  console.error(
                                    "Error searching entities:",
                                    error
                                  );
                                  setIsProfileSearching(false);
                                });
                            }, 300); // Wait 0.3 second after typing stops
                          } else {
                            setShowProfileEntitySuggestions(false);
                          }
                        }}
                      />
                      {isProfileSearching && (
                        <div className="absolute right-3 top-3">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {showProfileEntitySuggestions &&
                        profileEntitySuggestions.length > 0 && (
                          <div
                            ref={profileEntityDropdownRef}
                            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                          >
                            {profileEntitySuggestions.map((entity, index) => (
                              <div
                                key={index}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center text-xs"
                                onClick={() => {
                                  // First check for duplication
                                  setIsProfileSearching(true);
                                  apiService.get(`/lender/check-duplicate-entity?entity_id=${entity.id}`)
                                    .then(response => {
                                      if ((response as { exists: boolean }).exists) {
                                        toast.error("This entity already exists in the system.");
                                        setIsProfileSearching(false);
                                        return;
                                      }
                                      // If no duplication, proceed with selection
                                      handleProfileInputChange(
                                        "company_name",
                                        entity.name
                                      );
                                      // Store entity_id and entity_id_type temporarily (will be saved on API call)
                                      setSelectedEntityId(entity.id || "");
                                      setSelectedEntityIdType(entity.idType || "");
                                      setShowProfileEntitySuggestions(false);
                                      setIsProfileSearching(false);
                                    })
                                    .catch(error => {
                                      console.error("Error checking entity duplication:", error);
                                      toast.error("Error checking entity duplication.");
                                      setIsProfileSearching(false);
                                    });
                                }}
                              >
                                <div className="flex justify-between items-center w-full">
                                  <p className="truncate">{entity.name}</p>
                                  <span
                                    className={`w-2 h-2 rounded-full ${
                                      entity.status === "ACTIVE"
                                        ? "bg-green-500"
                                        : "bg-red-500"
                                    }`}
                                  ></span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Display Name</Label>
                    <Input
                      placeholder="Enter display name"
                      defaultValue={selectedLender.name}
                      onChange={(e) => handledisplayname(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zone</Label>

                    <ReactSelect
                      isMulti
                      options={zoneOptions}
                      value={
                        selectedLender?.profile?.zone
                          ? selectedLender.profile.zone
                              .split(",")
                              .map((zone) => ({
                                value: zone,
                                label: zone,
                              }))
                          : []
                      } // Ensure the value is an empty array if no zones are selected
                      onChange={(selectedOptions) => {
                        const selectedZones = selectedOptions?.length
                          ? selectedOptions
                              .map((option) => option.value)
                              .join(",")
                          : ""; // Set to an empty string if no options are selected
                        handleZoneChange(selectedZones); // Update the zone as a comma-separated string
                      }}
                      placeholder="Select zones"
                      className="basic-multi-select border-black"
                      classNamePrefix="select"
                      styles={{
                        control: (base, state) => ({
                          ...base,

                          borderRadius: "0.375rem", // Matches the border radius
                          border: state.isFocused
                            ? "3px solid #475569"
                            : "1px solid #e2e8f0", // Matches the Input border style
                          backgroundColor: "#ffffff",

                          borderColor: state.isFocused ? "black" : "black", // Always black border
                          boxShadow: "none", // Remove blue focus shadow
                          "&:hover": {
                            borderColor: "black", // Black border on hover
                          },
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "#f0f0f0", // Optional: Customize multi-value background
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "black", // Optional: Customize multi-value text color
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "black", // Optional: Customize remove icon color
                          "&:hover": {
                            backgroundColor: "black", // Optional: Customize remove hover background
                            color: "white", // Optional: Customize remove hover text color
                          },
                        }),
                      }}
                    />
                  </div>
                  <div>
                    <Label>Product Type</Label>

                    <ReactSelect
                      isMulti
                      options={LenderProduct}
                      value={
                        selectedLender?.profile?.product
                          ? selectedLender.profile.product
                              .split(",")
                              .map((product) => ({
                                value: product,
                                label: product,
                              }))
                          : []
                      }
                      onChange={(selectedOptions) => {
                        const selectedProducts =
                          selectedOptions?.map((option) => option.value) || []; // Convert to an array
                        handleProductChange(selectedProducts); // Update the product as an array
                      }}
                      placeholder="Select products"
                      className="basic-multi-select border-black"
                      classNamePrefix="select"
                      styles={{
                        control: (base, state) => ({
                          ...base,

                          borderRadius: "0.375rem", // Matches the border radius
                          border: state.isFocused
                            ? "3px solid #475569"
                            : "1px solid #e2e8f0", // Matches the Input border style
                          backgroundColor: "#ffffff",

                          borderColor: state.isFocused ? "black" : "black", // Always black border
                          boxShadow: "none", // Remove blue focus shadow
                          "&:hover": {
                            borderColor: "black", // Black border on hover
                          },
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "#f0f0f0", // Optional: Customize multi-value background
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "black", // Optional: Customize multi-value text color
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "black", // Optional: Customize remove icon color
                          "&:hover": {
                            backgroundColor: "black", // Optional: Customize remove hover background
                            color: "white", // Optional: Customize remove hover text color
                          },
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Address Line 1</Label>
                    <Input
                      placeholder="Enter address line 1"
                      onChange={(e) =>
                        handleProfileInputChange(
                          "address_line1",
                          e.target.value
                        )
                      }
                      defaultValue={selectedLender.profile.address_line1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      placeholder="Enter city"
                      onChange={(e) =>
                        handleProfileInputChange("address_city", e.target.value)
                      }
                      defaultValue={selectedLender.profile.address_city}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      placeholder="Enter state"
                      onChange={(e) =>
                        handleProfileInputChange(
                          "address_state",
                          e.target.value
                        )
                      }
                      defaultValue={selectedLender.profile.address_state}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pincode</Label>
                    <Input
                      placeholder="Enter pincode"
                      onChange={(e) =>
                        handleProfileInputChange(
                          "address_pincode",
                          e.target.value
                        )
                      }
                      defaultValue={selectedLender?.profile?.address_pincode}
                      className={pincodeError ? "border-red-500" : ""}
                    />
                    {pincodeError && (
                      <p className="text-red-500 text-sm">{pincodeError}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Type */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Account Type</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label>Account Type <span className="text-red-500">*</span></Label>
                    <Select
                      value={lenderAccountType}
                      onValueChange={(value: "Current Account" | "Virtual Account") => {
                        setLenderAccountType(value);
                        setAccountNumberMismatchError(null);
                        if (profileBankErrors.account_type) setProfileBankErrors((p) => ({ ...p, account_type: "" }));
                        handleProfileInputChange("account_type" as keyof Lender["profile"], value);
                      }}
                    >
                      <SelectTrigger disabled={isBankDetailsLocked} className={`bg-white border rounded-md ${profileBankErrors.account_type ? "border-red-500" : "border-gray-300"} ${isBankDetailsLocked ? "opacity-70 cursor-not-allowed" : ""}`}>
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                        <SelectItem value="Current Account" className="hover:bg-gray-100">
                          Current Account
                        </SelectItem>
                        <SelectItem value="Virtual Account" className="hover:bg-gray-100">
                          Virtual Account
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {profileBankErrors.account_type && <p className="text-sm text-red-500">{profileBankErrors.account_type}</p>}
                  </div>
                </div>

                {/* Same fields for both types; only labels for account number change */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Account Holder Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Enter account holder name"
                      defaultValue={(selectedLender?.profile as any)?.account_holder_name}
                      onChange={(e) => {
                        handleProfileInputChange("account_holder_name" as keyof Lender["profile"], e.target.value);
                        if (profileBankErrors.account_holder_name) setProfileBankErrors((p) => ({ ...p, account_holder_name: "" }));
                      }}
                      disabled={isBankDetailsLocked}
                      className={profileBankErrors.account_holder_name ? "border-red-500" : ""}
                    />
                    {profileBankErrors.account_holder_name && <p className="text-sm text-red-500">{profileBankErrors.account_holder_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Bank Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Enter bank name"
                      defaultValue={(selectedLender?.profile as any)?.bank_name}
                      onChange={(e) => {
                        handleProfileInputChange("bank_name" as keyof Lender["profile"], e.target.value);
                        if (profileBankErrors.bank_name) setProfileBankErrors((p) => ({ ...p, bank_name: "" }));
                      }}
                      disabled={isBankDetailsLocked}
                      className={profileBankErrors.bank_name ? "border-red-500" : ""}
                    />
                    {profileBankErrors.bank_name && <p className="text-sm text-red-500">{profileBankErrors.bank_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Branch Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Enter branch name"
                      defaultValue={(selectedLender?.profile as any)?.branch_name}
                      onChange={(e) => {
                        handleProfileInputChange("branch_name" as keyof Lender["profile"], e.target.value);
                        if (profileBankErrors.branch_name) setProfileBankErrors((p) => ({ ...p, branch_name: "" }));
                      }}
                      disabled={isBankDetailsLocked}
                      className={profileBankErrors.branch_name ? "border-red-500" : ""}
                    />
                    {profileBankErrors.branch_name && <p className="text-sm text-red-500">{profileBankErrors.branch_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{lenderAccountType === "Current Account" ? "Account Number" : "Virtual Account Number"} <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder={lenderAccountType === "Current Account" ? "Enter account number" : "Enter virtual account number"}
                      value={lenderAccountType === "Current Account" ? ((selectedLender?.profile as any)?.account_number ?? "") : ((selectedLender?.profile as any)?.virtual_account_number ?? "")}
                      onChange={(e) => {
                        const key = lenderAccountType === "Current Account" ? "account_number" : "virtual_account_number";
                        handleProfileInputChange(key as keyof Lender["profile"], e.target.value);
                        const reKey = lenderAccountType === "Current Account" ? "account_number_confirm" : "virtual_account_number_confirm";
                        const reEnter = (selectedLender?.profile as any)?.[reKey] ?? "";
                        if (e.target.value && reEnter) {
                          setAccountNumberMismatchError(e.target.value !== reEnter ? "Account numbers do not match" : null);
                        } else {
                          setAccountNumberMismatchError(null);
                        }
                        if (profileBankErrors.account_number) setProfileBankErrors((p) => ({ ...p, account_number: "" }));
                      }}
                      disabled={isBankDetailsLocked}
                      className={profileBankErrors.account_number ? "border-red-500" : ""}
                    />
                    {profileBankErrors.account_number && <p className="text-sm text-red-500">{profileBankErrors.account_number}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{lenderAccountType === "Current Account" ? "Re-Enter Account Number" : "Re-Enter Virtual Account Number"} <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder={lenderAccountType === "Current Account" ? "Re-enter account number" : "Re-enter virtual account number"}
                      value={lenderAccountType === "Current Account" ? ((selectedLender?.profile as any)?.account_number_confirm ?? "") : ((selectedLender?.profile as any)?.virtual_account_number_confirm ?? "")}
                      onChange={(e) => {
                        const reKey = lenderAccountType === "Current Account" ? "account_number_confirm" : "virtual_account_number_confirm";
                        handleProfileInputChange(reKey as keyof Lender["profile"], e.target.value);
                        const key = lenderAccountType === "Current Account" ? "account_number" : "virtual_account_number";
                        const accountNum = (selectedLender?.profile as any)?.[key] ?? "";
                        if (accountNum && e.target.value) {
                          setAccountNumberMismatchError(accountNum !== e.target.value ? "Account numbers do not match" : null);
                        } else {
                          setAccountNumberMismatchError(null);
                        }
                        if (profileBankErrors.account_number_confirm) setProfileBankErrors((p) => ({ ...p, account_number_confirm: "" }));
                      }}
                      className={(accountNumberMismatchError || profileBankErrors.account_number_confirm) ? "border-red-500" : ""}
                      disabled={isBankDetailsLocked}
                    />
                    {(accountNumberMismatchError || profileBankErrors.account_number_confirm) && (
                      <p className="text-sm text-red-500">{accountNumberMismatchError || profileBankErrors.account_number_confirm}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>IFSC Code <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Enter IFSC code"
                      defaultValue={(selectedLender?.profile as any)?.ifsc_code}
                      onChange={(e) => {
                        handleProfileInputChange("ifsc_code" as keyof Lender["profile"], e.target.value);
                        if (profileBankErrors.ifsc_code) setProfileBankErrors((p) => ({ ...p, ifsc_code: "" }));
                      }}
                      disabled={isBankDetailsLocked}
                      className={profileBankErrors.ifsc_code ? "border-red-500" : ""}
                    />
                    {profileBankErrors.ifsc_code && <p className="text-sm text-red-500">{profileBankErrors.ifsc_code}</p>}
                  </div>
                </div>
              </div>

              {/* Lender Payout */}
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold mb-4">
                      Lender Payout
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label>Payout Percentage (%)</Label>
                        <Input
                          type="number"
                          placeholder="Enter payout percentage"
                          min={0}
                          onChange={(e) =>
                            handleProfileInputChange(
                              "payout_percentage",
                              e.target.value
                            )
                          }
                          defaultValue={
                            selectedLender.profile.payout_percentage
                          }
                        />
                      </div>
                      <div>
                        <Label>Payout Type</Label>
                        <Select
                          onValueChange={handlePayoutChange}
                          defaultValue={selectedLender.profile.payout_type}
                        >
                          <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                            <SelectValue placeholder="Select lender type" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                            <SelectItem
                              value="Fixed"
                              className="hover:bg-gray-100"
                            >
                              Fixed
                            </SelectItem>
                            <SelectItem
                              className="hover:bg-gray-100"
                              value="Variable"
                            >
                              Variable
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold mb-4">
                      Lender Agreement
                    </h3>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-8">
                      {Lenderaggreement.map((data) => (
                        <div key={data.type}>
                          <DragAndDropUpload
                            onDrop={(acceptedFiles: File[]) =>
                              onDrop(acceptedFiles, data.type, data.filename)
                            }
                            className="h-[200px] w-full"
                          >
                            {!getDocument(data.type, documents) ? (
                              <>
                                <DragAndDropIcon className="mb-3" />
                                <DragAndDropTitle className="mb-3">
                                  {data.title}
                                </DragAndDropTitle>
                                <DragAndDropHelpText className="mb-3">
                                  Drag & Drop or Click to Upload
                                </DragAndDropHelpText>
                                <DragAndDropMaxSizeText className="text-xs">
                                  Max size: 5MB
                                </DragAndDropMaxSizeText>
                              </>
                            ) : (
                              <UploadPreview
                                document={getDocument(data.type, documents)!}
                                onDelete={() => {
                                  if (selectedLender?.id) {
                                    fetchALlDocuments();
                                  }
                                }}
                              />
                            )}
                          </DragAndDropUpload>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="float-right ">
                <Button className="mb-8" onClick={saveProfileChanges}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>



      {/* Add Lender Modal */}
      <Dialog open={isAddLenderOpen} onOpenChange={handleAddLenderOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lender</DialogTitle>
            <DialogDescription>
              Enter the details to add a new lender
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                User Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name || ""}
                onChange={handleLenderInputChange}
                placeholder="Enter lender name"
                className={`w-full border ${
                  addLenderErrors.name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {addLenderErrors.name && (
                <p className="text-red-500 text-xs mt-1">
                  {addLenderErrors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                value={formData.email || ""}
                onChange={handleLenderInputChange}
                type="email"
                placeholder="Enter email address"
                className={`w-full border ${
                  addLenderErrors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {addLenderErrors.email && (
                <p className="text-red-500 text-xs mt-1">
                  {addLenderErrors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input
                id="mobile"
                value={formData.mobile || ""}
                onChange={handleLenderInputChange}
                type="tel"
                placeholder="Enter mobile number"
                className="w-full border border-gray-300"
              />
            </div>

            <div className="space-y-2">
              <Label>Designation</Label>
              <Select
                value={formData.designation_id || ""}
                onValueChange={(value: any) =>
                  setFormData((prev) => ({
                    ...prev,
                    designation_id: value,
                  }))
                }
              >
                <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                  <SelectValue placeholder="Select Designation" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                  {lender_designation.map((option) => (
                    <SelectItem
                      className="hover:bg-gray-100"
                      key={option.id}
                      value={String(option.id)}
                    >
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                Lender Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.lender_type_id || ""}
                onValueChange={(value: any) => {
                  setFormData((prev) => ({
                    ...prev,
                    lender_type_id: value,
                  }));
                  // Clear error when field changes
                  if (addLenderErrors.lender_type_id) {
                    setAddLenderErrors((prev) => ({
                      ...prev,
                      lender_type_id: "",
                    }));
                  }
                }}
              >
                <SelectTrigger
                  className={`bg-white border rounded-md ${
                    addLenderErrors.lender_type_id
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <SelectValue placeholder="Select Lender Type" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                  {lender_type.map((option) => (
                    <SelectItem
                      className="hover:bg-gray-100"
                      key={option.id}
                      value={String(option.id)}
                    >
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {addLenderErrors.lender_type_id && (
                <p className="text-red-500 text-xs mt-1">
                  {addLenderErrors.lender_type_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mca">
                MCA Registered Entity Name{" "}
                <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="mca"
                  value={formData.mca || ""}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      mca: value,
                    }));

                    // Clear error for this field when it changes
                    if (addLenderErrors.mca) {
                      setAddLenderErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.mca;
                        return newErrors;
                      });
                    }

                    // Call search API after 3 characters
                    if (value.trim().length > 3) {
                      // Use debounce to prevent multiple API calls
                      if (searchTimeout.current) {
                        clearTimeout(searchTimeout.current);
                      }

                      searchTimeout.current = setTimeout(() => {
                        setIsSearching(true);
                        apiService
                          .post(`/customers/company/search`, { query: value })
                          .then((response) => {
                            setEntitySuggestions(
                              (response as { data: any[] }).data || []
                            );
                            setShowEntitySuggestions(true);
                            setIsSearching(false);
                          })
                          .catch((error) => {
                            console.error("Error searching entities:", error);
                            setIsSearching(false);
                          });
                      }, 300); // Wait 0.3 second after typing stops
                    } else {
                      setShowEntitySuggestions(false);
                    }
                  }}
                  placeholder="Enter MCA registered name"
                  className={`w-full border ${
                    addLenderErrors.mca ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {isSearching && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
                {showEntitySuggestions && entitySuggestions.length > 0 && (
                  <div
                    ref={entityDropdownRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                  >
                    {entitySuggestions.map((entity, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center text-xs"
                        onClick={() => {
                          // First check for duplication
                          setIsSearching(true);
                          apiService.get(`/lender/check-duplicate-entity?entity_id=${entity.id}`)
                            .then(response => {
                              if ((response as { exists: boolean }).exists) {
                                toast.error("This entity already exists in the system.");
                                setIsSearching(false);
                                return;
                              }
                              // If no duplication, proceed with selection
                              setFormData((prev) => ({
                                ...prev,
                                mca: entity.name,
                                entity_id: entity.id,
                                entity_id_type: entity.idType,
                              }));
                              setShowEntitySuggestions(false);
                              setIsSearching(false);
                            })
                            .catch(error => {
                              console.error("Error checking entity duplication:", error);
                              toast.error("Error checking entity duplication.");
                              setIsSearching(false);
                            });
                        }}
                      >
                        <div className="flex justify-between items-center w-full">
                          <p className="truncate">{entity.name}</p>
                          <span
                            className={`w-2 h-2 rounded-full ${
                              entity.status === "ACTIVE"
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {addLenderErrors.mca && (
                <p className="text-red-500 text-xs mt-1">
                  {addLenderErrors.mca}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="org">
                Organisation Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="org"
                value={formData.org || ""}
                onChange={handleLenderInputChange}
                placeholder="Enter organisation name"
                className={`w-full border ${
                  addLenderErrors.org ? "border-red-500" : "border-gray-300"
                }`}
              />
              {addLenderErrors.org && (
                <p className="text-red-500 text-xs mt-1">
                  {addLenderErrors.org}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="investment_preference">
                Investment Preference (Free Text)
              </Label>
              <Input
                id="investment_preference"
                value={formData.investment_preference || ""}
                onChange={handleLenderInputChange}
                placeholder="Enter Investment Preference"
                className="w-full border border-gray-300"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => handleAddLenderOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={addlender}>Add Lender</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={isAddUserOpen} onOpenChange={handleAddUserOpen}>
        <DialogContent className="sm:max-w-[550px] md:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Enter the details to add a new user
            </DialogDescription>
          </DialogHeader>

          <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
            <div className="space-y-4 py-2 px-1">
              <div className="space-y-2">
                <Label htmlFor="userName" className="text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={addUserData["userName"] || ""}
                  onChange={handleChangeadduser}
                  id="userName"
                  placeholder="Enter user name"
                  className={`w-full border border-gray-300 ${
                    addUserErrors.userName ? "border-red-500" : ""
                  }`}
                />
                {addUserErrors.userName && (
                  <p className="text-red-500 text-xs mt-1">
                    {addUserErrors.userName}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="userEmail" className="text-sm font-medium">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={addUserData["userEmail"] || ""}
                  onChange={handleChangeadduser}
                  id="userEmail"
                  type="email"
                  placeholder="Enter email address"
                  className={`w-full border border-gray-300 ${
                    addUserErrors.userEmail ? "border-red-500" : ""
                  }`}
                />
                {addUserErrors.userEmail && (
                  <p className="text-red-500 text-xs mt-1">
                    {addUserErrors.userEmail}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="userMobile" className="text-sm font-medium">
                  Mobile Number
                </Label>
                <Input
                  value={addUserData["userMobile"] || ""}
                  onChange={handleChangeadduser}
                  id="userMobile"
                  type="tel"
                  placeholder="Enter mobile number"
                  className="w-full border border-gray-300"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Designation</Label>
                <Select
                  defaultValue={addUserData["userDesignation_id"]}
                  onValueChange={(value: any) =>
                    setAddUserData((prev) => ({
                      ...prev,
                      userDesignation_id: value,
                    }))
                  }
                >
                  <SelectTrigger className="w-full bg-white border border-gray-300 rounded-md">
                    <SelectValue placeholder="Select Designation" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                    {lender_designation.map((option) => (
                      <SelectItem
                        className="hover:bg-gray-100"
                        key={option.id}
                        value={String(option.id)}
                      >
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="userlocation" className="text-sm font-medium">
                  Location
                </Label>
                <Input
                  value={addUserData["userlocation"] || ""}
                  onChange={handleChangeadduser}
                  id="userlocation"
                  placeholder="Enter location"
                  className="w-full border border-gray-300"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Product</Label>
                <Select
                  defaultValue={addUserData["lender_user_product_id"]}
                  onValueChange={(value: any) =>
                    setAddUserData((prev) => ({
                      ...prev,
                      lender_user_product_id: value,
                    }))
                  }
                >
                  <SelectTrigger className="w-full bg-white border border-gray-300 rounded-md">
                    <SelectValue placeholder="Select Product" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                    {lender_user_product_types.map((option) => (
                      <SelectItem
                        className="hover:bg-gray-100"
                        key={option.id}
                        value={String(option.id)}
                      >
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 mt-2 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => setIsAddUserOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addlenderuser}>Add User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditUserOpen} onOpenChange={handleEditUserOpen}>
        <DialogContent className="sm:max-w-[550px] md:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details</DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <>
              <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                <div className="space-y-4 py-2 px-1">
                  <div className="space-y-2">
                    <Label
                      htmlFor="editUserName"
                      className="text-sm font-medium"
                    >
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="editUserName"
                      value={selectedUser.name || ""}
                      onChange={(e) =>
                        handleEditUserChange("name", e.target.value)
                      }
                      className={`w-full border border-gray-300 ${
                        editUserErrors.name ? "border-red-500" : ""
                      }`}
                    />
                    {editUserErrors.name && (
                      <p className="text-red-500 text-xs mt-1">
                        {editUserErrors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="editUserEmail"
                      className="text-sm font-medium"
                    >
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="editUserEmail"
                      type="email"
                      value={selectedUser.email || ""}
                      onChange={(e) =>
                        handleEditUserChange("email", e.target.value)
                      }
                      className={`w-full border border-gray-300 ${
                        editUserErrors.email ? "border-red-500" : ""
                      }`}
                    />
                    {editUserErrors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {editUserErrors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="editUserMobile"
                      className="text-sm font-medium"
                    >
                      Mobile Number
                    </Label>
                    <Input
                      id="editUserMobile"
                      type="tel"
                      value={selectedUser.mobile || ""}
                      onChange={(e) =>
                        handleEditUserChange("mobile", e.target.value)
                      }
                      className="w-full border border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Designation</Label>
                    <Select
                      value={String(selectedUser.designation_id)}
                      onValueChange={(value: any) =>
                        handleEditUserChange("designation_id", value)
                      }
                    >
                      <SelectTrigger className="w-full bg-white border border-gray-300 rounded-md">
                        <SelectValue>
                          {lender_designation.find(
                            (option) =>
                              String(option.id) ===
                              String(selectedUser.designation_id)
                          )?.name || "Select Designation"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                        {lender_designation.map((option) => (
                          <SelectItem
                            className="hover:bg-gray-100"
                            key={option.id}
                            value={String(option.id)}
                          >
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="editUserLocation"
                      className="text-sm font-medium"
                    >
                      Location
                    </Label>
                    <Input
                      id="editUserLocation"
                      value={selectedUser.location || ""}
                      onChange={(e) =>
                        handleEditUserChange("location", e.target.value)
                      }
                      className="w-full border border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Product</Label>
                    <Select
                      value={String(selectedUser.lender_user_product_id)}
                      onValueChange={(value: any) =>
                        handleEditUserChange("lender_user_product_id", value)
                      }
                    >
                      <SelectTrigger className="w-full bg-white border border-gray-300 rounded-md">
                        <SelectValue>
                          {lender_user_product_types.find(
                            (option) =>
                              String(option.id) ===
                              String(selectedUser.lender_user_product_id)
                          )?.name || "Select Product"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                        {lender_user_product_types.map((option) => (
                          <SelectItem
                            className="hover:bg-gray-100"
                            key={option.id}
                            value={String(option.id)}
                          >
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 mt-2 border-t flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setIsEditUserOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={() => handleUpdateUser(selectedUser)}>
                  Save Changes
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {sheetLender && (
        <InvestmentPreferenceSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          lenderName={sheetLender.name}
          lenderId={String(sheetLender.id)}
          policy={sheetPolicy}
          onSave={async () => {
            await fetchPageDataWithFilters();
          }}
        />
      )}
      <LenderBankAccountsSheet
        open={isBankAccountsOpen}
        onOpenChange={setIsBankAccountsOpen}
        selectedLenderId={selectedLender?.id}
        lender={selectedLender?.name}
      />
    </div>
  );
};
